<template>
  <div class="min-h-screen bg-white text-zinc-950 lg:flex lg:pl-64">
    <Sidebar />
    <GlobalNavbar />
    <ApiSidebar :document="document" />
    <main class="min-w-0 flex-1 pt-16">
      <div class="mx-auto max-w-5xl px-6 py-12 lg:px-12">
        <p class="text-sm font-medium text-blue-600">Documentação da API</p>
        <h2 class="mt-3 break-words text-3xl font-semibold tracking-tight sm:text-4xl">{{ document.info?.title }}</h2>
        <div v-if="document.info?.description" class="prose prose-zinc mt-4 max-w-none text-zinc-600" v-html="markdownToHtml(document.info.description)"></div>
        <div v-if="document.servers?.length" class="mt-6 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <span class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Base URL</span>
            <button type="button" class="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-zinc-500 transition hover:bg-white hover:text-zinc-950" @click="openBaseUrlEditor">
              <PhPencil class="h-3.5 w-3.5" /> Editar
            </button>
            <button v-if="baseUrl !== defaultBaseUrl" type="button" class="text-xs text-zinc-400 hover:text-zinc-950" @click="restoreBaseUrl">Restaurar padrão</button>
          </div>
          <code class="mt-2 block break-all text-sm text-zinc-800">{{ baseUrl }}</code>
          <p class="mt-2 text-xs text-zinc-500">Essa URL será usada nos exemplos cURL e ao copiar endpoints.</p>
        </div>
        <div v-if="baseUrlEditorOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-emerald-950/30 px-4 backdrop-blur-sm" @click.self="baseUrlEditorOpen = false">
          <section class="w-full max-w-lg rounded-xl border border-zinc-200 bg-white p-6 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="base-url-title">
            <h3 id="base-url-title" class="text-lg font-semibold text-zinc-950">Editar Base URL</h3>
            <p class="mt-2 text-sm leading-6 text-zinc-600">Informe o endereço que será usado nos testes e exemplos de requisição.</p>
            <label for="api-base-url" class="mt-5 block text-sm font-medium text-zinc-700">Base URL</label>
            <input id="api-base-url" v-model="draftBaseUrl" type="url" class="mt-1.5 block w-full rounded-md border border-zinc-300 bg-white px-3 py-2.5 font-mono text-sm text-zinc-800 outline-none transition focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200" spellcheck="false" @keyup.enter="saveBaseUrl" />
            <div class="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" class="rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:border-zinc-500" @click="baseUrlEditorOpen = false">Cancelar</button>
              <button type="button" class="rounded-md bg-zinc-950 px-3 py-2 text-sm font-medium text-white hover:bg-zinc-800" @click="saveBaseUrl">Salvar</button>
            </div>
          </section>
        </div>
        <div class="mt-6 flex flex-wrap items-center gap-2" aria-label="Filtrar endpoints por método">
          <span class="mr-1 text-xs font-semibold uppercase tracking-wide text-zinc-400">Método</span>
          <button v-for="method in methods" :key="method" type="button" :class="methodFilterClass(method, selectedMethod === method)" class="rounded-md px-2.5 py-1.5 text-xs font-semibold uppercase transition" @click="selectedMethod = selectedMethod === method ? '' : method">
            {{ method }}
          </button>
        </div>

        <section v-for="tag in filteredGroupedTags" :key="tag.name" :id="slug(tag.name)" class="scroll-mt-8 pt-14 first:pt-12">
          <h3 class="flex items-center gap-3 text-2xl font-semibold tracking-tight">
            <span>{{ tag.name }}</span>
            <span v-if="tag['x-badge']" class="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700">{{ tag['x-badge'] }}</span>
          </h3>
          <div v-if="tag.description" class="prose prose-zinc mt-2 max-w-none text-sm text-zinc-600" v-html="markdownToHtml(tag.description)"></div>
            <ApiOperation
            v-for="item in tag.operations"
            :key="`${item.method}-${item.path}-${baseUrl}`"
            v-bind="item"
            :base-url="baseUrl"
            :auth-header="authHeader"
            :schemas="document.components?.schemas"
          />
        </section>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import ApiOperation from "../components/docs/ApiOperation.vue";
import ApiSidebar from "../components/docs/ApiSidebar.vue";
import Sidebar from "../components/layout/Sidebar.vue";
import GlobalNavbar from "../components/layout/GlobalNavbar.vue";
import type { ApiDocument } from "../components/docs/apiTypes";
import { markdownToHtml } from "../lib/markdown";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import { PhPencil } from "@phosphor-icons/vue";

const props = defineProps<{ document: ApiDocument }>();
const baseUrlStorageKey = `api-base-url:${props.document.info?.title || "documentation"}`;
const defaultBaseUrl = props.document.servers?.[0]?.url || "";
const baseUrl = ref(typeof window !== "undefined" ? window.localStorage.getItem(baseUrlStorageKey) || defaultBaseUrl : defaultBaseUrl);
const draftBaseUrl = ref(baseUrl.value);
const baseUrlEditorOpen = ref(false);
const openBaseUrlEditor = () => { draftBaseUrl.value = baseUrl.value; baseUrlEditorOpen.value = true; };
const saveBaseUrl = () => {
  baseUrl.value = draftBaseUrl.value.trim().replace(/\/$/, "");
  window.localStorage.setItem(baseUrlStorageKey, baseUrl.value);
  baseUrlEditorOpen.value = false;
};
const restoreBaseUrl = () => { baseUrl.value = defaultBaseUrl; window.localStorage.removeItem(baseUrlStorageKey); };
const slug = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const methods = ["get", "post", "put", "patch", "delete", "options", "head"];
const authHeader = Object.values(props.document.components?.securitySchemes || {})[0]?.name;

const tags: Array<{ name: string; description?: string; "x-badge"?: string }> = props.document.tags?.length
  ? props.document.tags
  : Array.from(new Set(Object.values(props.document.paths || {}).flatMap((pathItem) =>
      methods.flatMap((method) => pathItem[method]?.tags || [])
    ))).map((name) => ({ name }));
const groupedTags = tags.map((tag) => ({
  ...tag,
  operations: Object.entries(props.document.paths || {}).flatMap(([path, pathItem]) =>
    methods.filter((method) => pathItem[method]).map((method) => ({
      method,
      path,
      operation: {
        ...pathItem[method],
        parameters: [...(((pathItem as { parameters?: Array<Record<string, unknown>> }).parameters) || []), ...(pathItem[method].parameters || [])],
      },
    })).filter((item) => !item.operation.tags?.length || item.operation.tags.includes(tag.name))
  ),
}));
const selectedMethod = ref("");
const methodFilterClass = (method: string, selected: boolean) => {
  const colors: Record<string, [string, string]> = {
    get: ["bg-blue-600 text-white", "bg-blue-50 text-blue-700 hover:bg-blue-100"],
    post: ["bg-green-600 text-white", "bg-green-50 text-green-700 hover:bg-green-100"],
    put: ["bg-amber-500 text-white", "bg-amber-50 text-amber-700 hover:bg-amber-100"],
    patch: ["bg-purple-600 text-white", "bg-purple-50 text-purple-700 hover:bg-purple-100"],
    delete: ["bg-red-600 text-white", "bg-red-50 text-red-700 hover:bg-red-100"],
    options: ["bg-cyan-600 text-white", "bg-cyan-50 text-cyan-700 hover:bg-cyan-100"],
    head: ["bg-zinc-600 text-white", "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"],
  };
  return (colors[method] || colors.head)[selected ? 0 : 1];
};
const filteredGroupedTags = computed(() => selectedMethod.value
  ? groupedTags.map((tag) => ({ ...tag, operations: tag.operations.filter((item) => item.method === selectedMethod.value) })).filter((tag) => tag.operations.length)
  : groupedTags);
const scrollToHash = () => {
  const id = window.location.hash.slice(1);
  if (!id) return;
  window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
};
onMounted(async () => { await nextTick(); scrollToHash(); window.addEventListener("hashchange", scrollToHash); });
onUnmounted(() => window.removeEventListener("hashchange", scrollToHash));
</script>

<script lang="ts">
export default { name: "ApiDocs" };
</script>
